import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

import { errorMessage } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import { mailTemplatesApi } from '../../api/mail-templates-api';
import type { IMailSample } from '../../api/mail-template-contracts';

/**
 * What the preview is filled with: the placeholders' samples, or a submitted response picked from
 * survey-api's 20 latest that match the participant typed — the participant picker's shape, a
 * quarter-second after the typing stops. Shown only with `responses.browse`; survey-api judges.
 */

type Found =
  | { state: 'loading' }
  | { state: 'done'; samples: IMailSample[] }
  | { state: 'failed'; message: string };

const SETTLE_MS = 250;
const SAMPLE_DATA = 'pick:samples';

/** "Survey · cycle · submitted at" — what tells two of one participant's responses apart. */
function sampleDetail(sample: IMailSample): string {
  return [sample.survey, sample.cycle, sample.submittedAt ? formatDateTime(sample.submittedAt) : null]
    .filter(Boolean)
    .join(' · ');
}

export function SamplePicker({
  code,
  value,
  onChange,
  sampleName,
  participants = false
}: {
  code: string;
  value: Nullable<IMailSample>;
  onChange: (sample: Nullable<IMailSample>) => void;
  /** The participant the placeholders' samples name. */
  sampleName: Nullable<string>;
  /** A staff template's: list participants, not submitted responses. */
  participants?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [settled, setSettled] = React.useState('');
  const [found, setFound] = React.useState<Found>({ state: 'loading' });
  const labelId = React.useId();
  const sampleData = sampleName ? `Sample data (${sampleName})` : 'Sample data';

  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(query.trim()), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  React.useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setFound({ state: 'loading' });
    mailTemplatesApi.samples(code, settled, controller.signal).then(
      (samples) => {
        if (!controller.signal.aborted) setFound({ state: 'done', samples });
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setFound({ state: 'failed', message: errorMessage(error, 'Responses could not be searched') });
        }
      }
    );
    return () => controller.abort();
  }, [open, settled, code]);

  const choose = (sample: Nullable<IMailSample>) => {
    onChange(sample);
    setOpen(false);
  };

  const line = (text: string, failed = false) => (
    <p role={failed ? 'alert' : undefined} className={cn('px-2 py-2 text-xs text-muted-foreground', failed && 'text-destructive')}>
      {text}
    </p>
  );

  return (
    <span className="flex min-w-0 flex-[3_1_17rem] items-center gap-2">
      <span id={labelId} className="text-xs whitespace-nowrap text-muted-foreground">
        Filled with
      </span>
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next) {
            setQuery('');
            setSettled('');
          }
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-labelledby={`${labelId} ${labelId}-value`}
            className="h-8 min-w-0 flex-1 justify-between gap-2 px-2.5 text-[0.8125rem] font-normal"
            data-sample-picker=""
          >
            <span id={`${labelId}-value`} className="truncate">
              {value ? (
                <>
                  <span className="font-medium">{value.participant}</span>
                  <span className="text-muted-foreground"> · {sampleDetail(value)}</span>
                </>
              ) : (
                sampleData
              )}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(26rem,calc(100vw-2rem))] p-0">
          <Command shouldFilter={false} label="Fill the preview with">
            <CommandInput value={query} onValueChange={setQuery} placeholder="A participant's name" />
            <CommandList className="max-h-[min(22rem,60dvh)] py-1">
              <CommandGroup>
                <CommandItem value={SAMPLE_DATA} onSelect={() => choose(null)}>
                  {sampleData}
                </CommandItem>
              </CommandGroup>
              <CommandGroup heading={participants ? 'Participants' : 'Submitted responses'}>
                {found.state === 'loading' && line('Searching…')}
                {found.state === 'failed' && line(found.message, true)}
                {found.state === 'done' &&
                  found.samples.length === 0 &&
                  line(participants ? 'No participant matches.' : 'No submitted response matches.')}
                {found.state === 'done' &&
                  found.samples.map((sample) => {
                    const key = sample.responseId ?? `${sample.participantId}:${sample.survey}`;
                    return (
                      <CommandItem
                        key={key}
                        value={`sample:${key}`}
                        onSelect={() => choose(sample)}
                        aria-labelledby={`${labelId}-${key}-who ${labelId}-${key}-what`}
                      >
                        <span className="grid min-w-0">
                          <span id={`${labelId}-${key}-who`} className="truncate font-medium">
                            {sample.participant}
                          </span>
                          <span id={`${labelId}-${key}-what`} className="truncate text-xs text-muted-foreground">
                            {sampleDetail(sample)}
                          </span>
                        </span>
                      </CommandItem>
                    );
                  })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </span>
  );
}
